import React from "react";
import { useTranslation } from "react-i18next";

import { InfoPlate } from "fe-shared/src/components";
import AssigneeTransactionSelect from "components/AssigneeTransactionSelect";

import {
  InvalidationStatus,
  PaperFormIndicator,
  TransactionStatusPlate,
} from "fe-shared-transactions/src/components";
import { formatTransactionNumber } from "fe-shared-transactions/src/helpers/format";
import {
  Transaction,
  TransactionStatus as StatusCode,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { priceToFormat } from "fe-shared-transactions/src/helpers/invoice";

import styles from "./TransactionStatus.module.scss";

interface Props {
  canAssign: boolean;
  transaction: Transaction;
}

const TransactionStatus = ({ canAssign, transaction }: Props) => {
  const {
    assignee,
    statusCode,
    isPaperForm,
    isAdjustable,
    transactionNumber,
    regulatoryYear,
    requestNumber,
  } = transaction;

  const isAssigned = !!assignee?.userId;
  const { t } = useTranslation();
  const shouldShowPaymentAmount =
    statusCode !== StatusCode.Invoiced &&
    transaction?.paymentAmount !== null &&
    transaction?.paymentAmount !== undefined &&
    transaction.transactionType !== TransactionType.Retreading;

  return (
    <div className={styles.header}>
      <div className={styles.status}>
        <div className={styles.statusTransactionNumberInfo}>
          <InfoPlate variant="black">
            {formatTransactionNumber(transactionNumber)}
          </InfoPlate>
        </div>
        <TransactionStatusPlate
          isAdjustable={isAdjustable}
          statusCode={statusCode}
          className={styles.statusInfoPlate}
        />
        {shouldShowPaymentAmount && (
          <div className={styles.statusInfoPlate}>
            <InfoPlate variant="info">
              {t("transactions.details.paymentAmountValue", {
                paymentAmount: priceToFormat(transaction?.paymentAmount || 0),
              })}
            </InfoPlate>
          </div>
        )}
        {regulatoryYear && (
          <div className={styles.statusInfoPlate}>
            <InfoPlate variant="neutral">
              {t("transactions.details.regYear.statusPlate")}
            </InfoPlate>
          </div>
        )}
        {requestNumber && (
          <div className={styles.statusInfoPlate}>
            <InfoPlate variant="purple">
              {t("transactions.details.byRequest.statusPlate")}
            </InfoPlate>
          </div>
        )}
        <PaperFormIndicator isPaperForm={isPaperForm} />
        <div className={styles.statusAssign}>
          {isAssigned && (
            <span className={styles.statusAssignLabel}>
              {t("transactions.details.assignedTo")}
            </span>
          )}
          <AssigneeTransactionSelect
            canAssign={canAssign}
            transaction={transaction}
            assigneeId={assignee?.userId}
            assigneeName={
              assignee?.displayName ||
              (assignee
                ? t("transactions.details.undefinedUser")
                : undefined)
            }
          />
        </div>
      </div>
      <InvalidationStatus transactionOrTransfer={transaction} />
    </div>
  );
};

export default TransactionStatus;
